import type { MarketLevelResult } from "@shared/labs/candidate-assessment";
import { isInterviewDecision } from "@shared/labs/three-axis";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  ExternalLink,
  History,
  Loader2,
  Search,
  Trash2,
  TrendingUp,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { rpcClient } from "@/lib/rpc";
import { useInferMarketLevel } from "@/pages/labs/_shared/use-infer-market-level";
import { useMarkdownHtml } from "@/pages/labs/_shared/use-markdown-html";
import { ProfileHistoryDrawer } from "./profile-history-drawer";

interface EvaluationRow {
  id: string;
  candidateName: string | null;
  disciplineSlug: string | null;
  targetLevel: string | null;
  summary: {
    recommendedLevel?: string;
    /** Hire-leaning ("interview") vs. negative ("reject") on the source
     *  assessment. Surfaced on the row so the reviewer sees the most
     *  load-bearing signal before clicking. Undefined for legacy evals
     *  where the model didn't emit a three-axis decision. */
    decision?: "interview" | "reject";
  };
  /** Compact market-level badge data — present when inference has already
   *  run on this evaluation. Null means the field was never computed. */
  marketLevel: { marketLevel: string; cglDisplayName: string } | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** Live (non-rejected, non-reverted) corrections on this evaluation.
   *  Drives the "edited" pip on the row so the operator can see at a
   *  glance which archived rows carry CHRO feedback. */
  correctionCount: number;
}

function humanizeAge(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days <= 0) {
    const hours = Math.floor(diff / 3_600_000);
    if (hours <= 0) return "agora há pouco";
    if (hours === 1) return "há 1 hora";
    return `há ${hours} horas`;
  }
  if (days === 1) return "ontem";
  if (days < 30) return `há ${days} dias`;
  const months = Math.floor(days / 30);
  if (months === 1) return "há 1 mês";
  if (months < 12) return `há ${months} meses`;
  const years = Math.floor(months / 12);
  return years === 1 ? "há 1 ano" : `há ${years} anos`;
}

/** "28 abr · 14:35" — kept short on the row meta strip. */
function formatRowDate(iso: string): string {
  const d = new Date(iso);
  return (
    d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }) +
    " · " +
    d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
  );
}

function disciplineLabel(slug: string | null): string {
  if (!slug) return "Sem disciplina";
  return slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Group an evaluation row into one of five time buckets so the archive
 *  reads like an inbox instead of a flat list. The bucket boundaries are
 *  intentionally coarse — "esta semana" means within 7 days, not the
 *  ISO week, because reviewers think in "this past week" terms when
 *  scanning hiring activity. */
type TimeBucket = "hoje" | "ontem" | "esta_semana" | "este_mes" | "mais_antigos";

const BUCKET_LABEL: Record<TimeBucket, string> = {
  hoje: "Hoje",
  ontem: "Ontem",
  esta_semana: "Esta semana",
  este_mes: "Este mês",
  mais_antigos: "Mais antigos",
};

const BUCKET_ORDER: TimeBucket[] = ["hoje", "ontem", "esta_semana", "este_mes", "mais_antigos"];

function bucketFor(iso: string): TimeBucket {
  const ms = Date.now() - new Date(iso).getTime();
  const days = ms / 86_400_000;
  if (days < 1) return "hoje";
  if (days < 2) return "ontem";
  if (days < 7) return "esta_semana";
  if (days < 30) return "este_mes";
  return "mais_antigos";
}

/** Compute the row tone from the model's hire/no-hire decision. The earlier
 *  level-vs-target heuristic produced "on" for every row in production
 *  because the agent emits the role's target as the recommendation; the
 *  signal that actually matters is the binary interview/reject call. */
type DecisionTone = "ok" | "no" | "neutral";

function decisionTone(decision: "interview" | "reject" | undefined): DecisionTone {
  if (decision === "interview") return "ok";
  if (decision === "reject") return "no";
  return "neutral";
}

const PAGE_SIZE = 25;

interface EvaluationsListResponse {
  rows: EvaluationRow[];
  total: number;
  limit: number;
  offset: number;
  /** Distinct discipline slugs across the whole table — populates the
   *  filter chips so they're stable across pages. */
  disciplines: string[];
}

export function EvaluationsSection({ discipline: pageDiscipline }: { discipline?: string } = {}) {
  const { toast } = useToast();
  // Search + discipline filter live on the server now so pagination
  // composes with them. We debounce the search input so each keystroke
  // doesn't fire a query.
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  // The page-level discipline filter (from the parent page) overrides the
  // internal chip filter. When pageDiscipline is set, the chip row is hidden
  // and we use the page selection directly.
  const [localDiscipline, setLocalDiscipline] = useState<string | "all">("all");
  const discipline = pageDiscipline ?? localDiscipline;
  const [page, setPage] = useState(0);
  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 250);
    return () => window.clearTimeout(t);
  }, [searchInput]);
  // Reset to page 0 whenever the filter narrows; otherwise the user can
  // land on an empty page (e.g. they're on page 5 of "all" then filter
  // to a single-page discipline). The deps tell React *when* to fire —
  // the body intentionally doesn't read them.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deps are the trigger, body uses none of them
  useEffect(() => {
    setPage(0);
  }, [search, discipline, pageDiscipline]);

  const offset = page * PAGE_SIZE;
  const queryKey = useMemo(
    () => ["labs", "evaluationsList", { offset, search, discipline }] as const,
    [offset, search, discipline],
  );

  /** Open the full candidate-intelligence report in a NEW TAB so the
   *  reviewer keeps the archive list available for the next jump. The
   *  candidate page reads `?evaluationId=` on mount and hydrates the
   *  stored assessment via openPriorEvaluation, no LinkedIn re-fetch. */
  const openInReport = (id: string) => {
    if (typeof window === "undefined") return;
    const url = `/labs/candidate-intelligence?evaluationId=${encodeURIComponent(id)}`;
    // noopener to keep the new tab from gaining window.opener access; the
    // current tab is unaffected so the user can fan out to several reports
    // from a single archive view.
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const { data, isLoading } = useQuery<EvaluationsListResponse>({
    queryKey,
    queryFn: () =>
      rpcClient.labs.evaluationsList({
        limit: PAGE_SIZE,
        offset,
        q: search.trim() || undefined,
        discipline: discipline !== "all" ? discipline : undefined,
      }),
    placeholderData: (prev) => prev,
  });

  const [pendingDelete, setPendingDelete] = useState<EvaluationRow | null>(null);
  const [pendingPromote, setPendingPromote] = useState<EvaluationRow | null>(null);
  const [detailRow, setDetailRow] = useState<EvaluationRow | null>(null);
  const [historyRow, setHistoryRow] = useState<EvaluationRow | null>(null);
  /** Optimistic overlay: market-level results that came back from infer-market-level
   *  but haven't been re-fetched from the server yet. Keyed by evaluation ID.
   *  Applied on top of the list data so the badge appears immediately. */
  const [marketLevelOverlay, setMarketLevelOverlay] = useState<
    Map<string, { marketLevel: string; cglDisplayName: string }>
  >(new Map());

  function applyMarketLevel(evaluationId: string, result: MarketLevelResult) {
    setMarketLevelOverlay((prev) => {
      const next = new Map(prev);
      next.set(evaluationId, {
        marketLevel: result.marketLevel,
        cglDisplayName: result.cglDisplayName,
      });
      return next;
    });
  }

  const remove = useMutation({
    mutationFn: async (id: string) => {
      await rpcClient.labs.evaluationsRemove({ id });
    },
    onSuccess: () => {
      setPendingDelete(null);
      queryClient.invalidateQueries({ queryKey: ["labs", "evaluationsList"] });
      toast({ title: "Avaliação removida do arquivo" });
    },
    onError: (err: Error) => {
      toast({ title: "Falha ao remover", description: err.message, variant: "destructive" });
    },
  });

  const promote = useMutation({
    mutationFn: async ({
      id,
      decision,
    }: {
      id: string;
      decision: "hired" | "rejected" | "pending";
    }) => {
      return rpcClient.labs.evaluationsPromote({ id, decision });
    },
    onSuccess: (_data, vars) => {
      setPendingPromote(null);
      queryClient.invalidateQueries({ queryKey: ["labs", "calibrationCorpusList"] });
      const titleByDecision: Record<typeof vars.decision, string> = {
        hired: "Salvo como contratado",
        rejected: "Salvo como não contratado",
        pending: "Salvo como pendente",
      };
      const descByDecision: Record<typeof vars.decision, string> = {
        hired:
          "Perfil entra na base como exemplo positivo. Ajuste as notas em Perfis de referência.",
        rejected:
          "Perfil entra na base como contra-exemplo. Ajuste as notas em Perfis de referência.",
        pending:
          "Salvo na base, mas excluído das calibrações até você decidir contratado/não contratado.",
      };
      toast({
        title: titleByDecision[vars.decision],
        description: descByDecision[vars.decision],
      });
    },
    onError: (err: Error) => {
      toast({ title: "Falha ao salvar", description: err.message, variant: "destructive" });
    },
  });

  const total = data?.total ?? 0;
  const disciplines = data?.disciplines ?? [];

  // Apply the optimistic overlay so market-level badges appear immediately
  // after the infer call returns, without waiting for a full re-fetch.
  const rows = useMemo(() => {
    const base = data?.rows ?? [];
    if (marketLevelOverlay.size === 0) return base;
    return base.map((r) => {
      const overlay = marketLevelOverlay.get(r.id);
      return overlay ? { ...r, marketLevel: overlay } : r;
    });
  }, [data?.rows, marketLevelOverlay]);

  // Bucket the page's rows into time windows so the archive reads as a
  // chronological journal — recent activity at the top, older work
  // collapsed beneath. Empty buckets are skipped on render.
  const buckets = useMemo(() => {
    const m: Record<TimeBucket, EvaluationRow[]> = {
      hoje: [],
      ontem: [],
      esta_semana: [],
      este_mes: [],
      mais_antigos: [],
    };
    for (const r of rows) m[bucketFor(r.createdAt)].push(r);
    return m;
  }, [rows]);

  // First render only — keep the empty-state copy from showing while
  // a search/page change is in flight. `placeholderData` keeps the prior
  // page's rows visible during the swap, so we only show the spinner on
  // the very first load.
  if (isLoading && !data) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Truly-empty archive (no filter applied, no rows). Distinct from
  // "no results for this search" which is handled below the toolbar.
  if (data && total === 0 && search.trim().length === 0 && discipline === "all") {
    return (
      <div className="eval-empty">
        <div className="eval-empty-rule" />
        <h4 className="eval-empty-title">O arquivo está vazio</h4>
        <p className="eval-empty-body">
          Toda avaliação que você rodar em{" "}
          <code className="eval-empty-code">/labs/candidate-intelligence</code> aparece aqui
          automaticamente. Volte depois da próxima rodada.
        </p>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);

  return (
    <div className="eval-archive">
      <div className="eval-archive-toolbar" data-testid="eval-archive-toolbar">
        <div className="eval-archive-search">
          <Search className="eval-archive-search-icon" aria-hidden />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Buscar por candidato ou avaliador…"
            className="eval-archive-search-input"
            aria-label="Buscar avaliações"
            data-testid="eval-archive-search"
          />
          {searchInput && (
            <button
              type="button"
              className="eval-archive-search-clear"
              onClick={() => setSearchInput("")}
              aria-label="Limpar busca"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {disciplines.length > 1 && !pageDiscipline && (
          <div className="eval-archive-chips" role="tablist" aria-label="Filtrar por disciplina">
            <button
              type="button"
              role="tab"
              aria-selected={localDiscipline === "all"}
              className={`eval-chip${localDiscipline === "all" ? " is-active" : ""}`}
              onClick={() => setLocalDiscipline("all")}
            >
              <span className="eval-chip-label">Todas</span>
            </button>
            {disciplines.map((d) => (
              <button
                key={d}
                type="button"
                role="tab"
                aria-selected={localDiscipline === d}
                className={`eval-chip${localDiscipline === d ? " is-active" : ""}`}
                onClick={() => setLocalDiscipline(d)}
              >
                <span className="eval-chip-label">{disciplineLabel(d)}</span>
              </button>
            ))}
          </div>
        )}
        <div className="eval-archive-stat" aria-live="polite">
          <span className="eval-archive-stat-num">{total}</span>
          <span className="eval-archive-stat-label">
            {total === 1 ? "avaliação" : "avaliações"}
          </span>
        </div>
      </div>

      {total === 0 ? (
        <div className="eval-archive-noresults">
          Nenhum candidato bate com este filtro. Tente outro termo ou{" "}
          <button
            type="button"
            className="eval-archive-link"
            onClick={() => {
              setSearchInput("");
              setSearch("");
              setLocalDiscipline("all");
            }}
          >
            limpe os filtros
          </button>
          .
        </div>
      ) : (
        <>
          <div className="eval-archive-stream">
            {BUCKET_ORDER.map((b) => {
              const bucketRows = buckets[b];
              if (bucketRows.length === 0) return null;
              return (
                <section key={b} className="eval-bucket">
                  <header className="eval-bucket-head">
                    <h4 className="eval-bucket-label">{BUCKET_LABEL[b]}</h4>
                    <span className="eval-bucket-rule" aria-hidden />
                    <span className="eval-bucket-count">
                      {bucketRows.length} {bucketRows.length === 1 ? "avaliação" : "avaliações"}
                    </span>
                  </header>
                  <ol className="eval-bucket-rows">
                    {bucketRows.map((row, i) => (
                      <ArchiveRow
                        key={row.id}
                        row={row}
                        delayMs={Math.min(i * 24, 240)}
                        onOpen={() => setDetailRow(row)}
                        onOpenReport={() => openInReport(row.id)}
                        onHistory={() => setHistoryRow(row)}
                        onPromote={() => setPendingPromote(row)}
                        onDelete={() => setPendingDelete(row)}
                        onMarketLevelComputed={(result) => {
                          applyMarketLevel(row.id, result);
                        }}
                      />
                    ))}
                  </ol>
                </section>
              );
            })}
          </div>
          {totalPages > 1 && (
            <nav
              className="eval-archive-pagination"
              aria-label="Paginação"
              data-testid="eval-archive-pagination"
            >
              <button
                type="button"
                className="eval-archive-page-btn"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                aria-label="Página anterior"
                data-testid="eval-archive-page-prev"
              >
                <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
              </button>
              <span className="eval-archive-page-label" aria-live="polite">
                {pageStart}–{pageEnd} de {total}
              </span>
              <button
                type="button"
                className="eval-archive-page-btn"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                aria-label="Próxima página"
                data-testid="eval-archive-page-next"
              >
                <ChevronRight className="h-3.5 w-3.5" aria-hidden />
              </button>
            </nav>
          )}
        </>
      )}

      <EvaluationDetailOverlay
        open={detailRow !== null}
        row={detailRow}
        onClose={() => setDetailRow(null)}
        onOpenReport={openInReport}
        onMarketLevelComputed={(evaluationId, result) => {
          // Propagate back to the list row badge.
          applyMarketLevel(evaluationId, result);
        }}
      />

      <ProfileHistoryDrawer
        open={historyRow !== null}
        evaluationId={historyRow?.id ?? null}
        candidateName={historyRow?.candidateName ?? null}
        onClose={() => setHistoryRow(null)}
      />

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(v) => {
          if (!v && !remove.isPending) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader className="text-left sm:text-left">
            <AlertDialogTitle className="text-left">
              Remover avaliação de {pendingDelete?.candidateName ?? "este candidato"}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left">
              A avaliação sai do arquivo. Correções parqueadas e perfis de referência não são
              afetados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => pendingDelete && remove.mutate(pendingDelete.id)}
              disabled={remove.isPending}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {remove.isPending ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin mr-2" />
                  Removendo…
                </>
              ) : (
                "Remover"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pendingPromote !== null}
        onOpenChange={(v) => {
          if (!v && !promote.isPending) setPendingPromote(null);
        }}
      >
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader className="text-left sm:text-left">
            <AlertDialogTitle className="text-left">
              Usar {pendingPromote?.candidateName ?? "este perfil"} como referência?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left">
              O perfil vai para a base de referência com as notas extraídas desta avaliação. Como
              esta pessoa foi tratada na nossa pipeline?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid gap-2 my-2">
            <button
              type="button"
              data-testid="promote-decision-hired"
              onClick={() =>
                pendingPromote && promote.mutate({ id: pendingPromote.id, decision: "hired" })
              }
              disabled={promote.isPending}
              className="text-left rounded-md border border-emerald-200 bg-emerald-50/40 hover:bg-emerald-50 hover:border-emerald-300 px-4 py-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="text-sm font-semibold text-emerald-900">Contratamos</div>
              <div className="text-xs text-emerald-800/80 mt-0.5">
                Entra como exemplo positivo — calibra o que esperamos de quem queremos.
              </div>
            </button>
            <button
              type="button"
              data-testid="promote-decision-rejected"
              onClick={() =>
                pendingPromote && promote.mutate({ id: pendingPromote.id, decision: "rejected" })
              }
              disabled={promote.isPending}
              className="text-left rounded-md border border-rose-200 bg-rose-50/40 hover:bg-rose-50 hover:border-rose-300 px-4 py-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="text-sm font-semibold text-rose-900">Não contratamos</div>
              <div className="text-xs text-rose-800/80 mt-0.5">
                Entra como contra-exemplo — calibra o que evitamos.
              </div>
            </button>
            <button
              type="button"
              data-testid="promote-decision-pending"
              onClick={() =>
                pendingPromote && promote.mutate({ id: pendingPromote.id, decision: "pending" })
              }
              disabled={promote.isPending}
              className="text-left rounded-md border border-slate-200 bg-slate-50/60 hover:bg-slate-100 hover:border-slate-300 px-4 py-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="text-sm font-semibold text-slate-900">Decisão pendente</div>
              <div className="text-xs text-slate-600 mt-0.5">
                Salva o perfil mas mantém fora das calibrações até você decidir contratado ou não.
              </div>
            </button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={promote.isPending}>Cancelar</AlertDialogCancel>
            {promote.isPending && (
              <span className="inline-flex items-center text-xs text-slate-500 px-3">
                <Loader2 className="h-3 w-3 animate-spin mr-2" />
                Salvando…
              </span>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * Single row in the archive stream. Designed to be high-density: a level
 * chip leads (tone reflects how the recommended level sits vs. the role's
 * target — green above, navy on, red below), a strong candidate name as
 * the click affordance, then a meta strip with discipline + target +
 * evaluator + date in mono caption type. Action icons sit on the far
 * right and only reveal full opacity on row hover.
 */
function ArchiveRow({
  row,
  delayMs,
  onOpen,
  onOpenReport,
  onHistory,
  onPromote,
  onDelete,
  onMarketLevelComputed,
}: {
  row: EvaluationRow;
  delayMs: number;
  onOpen: () => void;
  onOpenReport: () => void;
  onHistory: () => void;
  onPromote: () => void;
  onDelete: () => void;
  onMarketLevelComputed?: (result: MarketLevelResult) => void;
}) {
  const tone = decisionTone(row.summary.decision);
  const decisionLabel =
    row.summary.decision === "interview"
      ? "Entrevistar"
      : row.summary.decision === "reject"
        ? "Não entrevistar"
        : null;
  const { inferMarketLevel, loading: mlLoading, error: mlError } = useInferMarketLevel();

  async function handleComputeMarketLevel() {
    const result = await inferMarketLevel(row.id);
    if (result) onMarketLevelComputed?.(result);
  }

  return (
    <li
      className="eval-arc-row"
      style={{ animationDelay: `${delayMs}ms` }}
      data-testid={`eval-row-${row.id}`}
    >
      <span className={`eval-arc-stripe tone-${tone}`} aria-hidden />
      <button
        type="button"
        className="eval-arc-click"
        onClick={onOpen}
        data-testid={`eval-open-${row.id}`}
      >
        {/* Level chip stays neutral — the row stripe (left) and decision pill
            (in the meta strip below) already encode the interview/no-interview
            tone, and three rose elements per row read as visual noise. The
            chip's role is the level number, not the decision. */}
        <span className="eval-arc-level tone-neutral">
          {row.summary.recommendedLevel ? (
            <span className="eval-arc-level-num">{row.summary.recommendedLevel}</span>
          ) : (
            <span className="eval-arc-level-num">—</span>
          )}
        </span>
        <span className="eval-arc-body">
          <span className="eval-arc-name">
            {row.candidateName ?? "— sem nome —"}
            {row.correctionCount > 0 && (
              <span
                className="eval-arc-edited"
                title={`${row.correctionCount} correç${row.correctionCount === 1 ? "ão" : "ões"} feita${row.correctionCount === 1 ? "" : "s"} nesta avaliação — abra "Histórico de mudanças" para revisar.`}
                data-testid={`eval-edited-${row.id}`}
              >
                editado {row.correctionCount}x
              </span>
            )}
          </span>
          <span className="eval-arc-meta">
            {decisionLabel && (
              <span
                className={`eval-arc-meta-item eval-arc-decision tone-${tone}`}
                data-testid={`eval-decision-${row.id}`}
              >
                {decisionLabel}
              </span>
            )}
            {row.marketLevel ? (
              <span
                className="eval-arc-meta-item eval-arc-market-level"
                data-testid={`eval-market-level-badge-${row.id}`}
                title={`Salário inferido a partir do histórico profissional · ${row.marketLevel.cglDisplayName} (${row.marketLevel.marketLevel})`}
              >
                Salário inferido: {row.marketLevel.cglDisplayName}
              </span>
            ) : (
              <span
                className="eval-arc-meta-item eval-arc-market-level-empty"
                data-testid={`eval-market-level-empty-${row.id}`}
              >
                —
              </span>
            )}
            {row.disciplineSlug && (
              <span className="eval-arc-meta-item eval-arc-discipline">
                {disciplineLabel(row.disciplineSlug)}
              </span>
            )}
            {row.createdBy && (
              <span
                className={`eval-arc-meta-item eval-arc-actor${
                  row.createdBy === "anonymous" ? " eval-arc-actor-unknown" : ""
                }`}
                title={
                  row.createdBy === "anonymous"
                    ? "Avaliação anterior ao registro de autoria — autor real desconhecido."
                    : undefined
                }
              >
                {row.createdBy === "anonymous" ? "autor desconhecido" : row.createdBy}
              </span>
            )}
            <span className="eval-arc-meta-item eval-arc-date" title={humanizeAge(row.createdAt)}>
              {formatRowDate(row.createdAt)}
            </span>
          </span>
          {mlError && (
            <span
              className="eval-arc-market-level-error"
              data-testid={`eval-market-level-error-${row.id}`}
            >
              {mlError}
            </span>
          )}
        </span>
      </button>
      <div className="eval-arc-actions">
        {!row.marketLevel && (
          <Button
            size="sm"
            variant="ghost"
            className="eval-arc-act-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleComputeMarketLevel();
            }}
            disabled={mlLoading}
            title="Computar nível de mercado"
            data-testid={`eval-compute-market-level-${row.id}`}
          >
            {mlLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <TrendingUp className="h-3.5 w-3.5" />
            )}
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="eval-arc-act-btn eval-arc-act-primary"
          onClick={onOpenReport}
          title="Abrir relatório completo no Inteligência de Candidatos"
          data-testid={`eval-open-report-${row.id}`}
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="eval-arc-act-btn"
          onClick={onHistory}
          title="Ver histórico de mudanças"
          data-testid={`eval-history-${row.id}`}
        >
          <History className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="eval-arc-act-btn"
          onClick={onPromote}
          title="Usar como referência"
          data-testid={`eval-promote-${row.id}`}
        >
          <Copy className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="eval-arc-act-btn eval-arc-act-danger"
          onClick={onDelete}
          title="Remover do arquivo"
          data-testid={`eval-remove-${row.id}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </li>
  );
}

function EvaluationDetailOverlay({
  open,
  row,
  onClose,
  onOpenReport,
  onMarketLevelComputed,
}: {
  open: boolean;
  row: EvaluationRow | null;
  onClose: () => void;
  onOpenReport: (id: string) => void;
  onMarketLevelComputed?: (evaluationId: string, result: MarketLevelResult) => void;
}) {
  const detailQueryKey = ["labs", "evaluationsGet", row?.id, "detail"] as const;
  const { data, isLoading } = useQuery<{
    id: string;
    assessment: Record<string, unknown>;
    createdAt: string;
  }>({
    queryKey: detailQueryKey,
    queryFn: () => rpcClient.labs.evaluationsGet({ id: row!.id }),
    enabled: open && row !== null,
  });

  const { inferMarketLevel, loading: mlLoading, error: mlError } = useInferMarketLevel();
  /** Holds the inferred result locally so the overlay re-renders immediately
   *  without relying on the global queryClient singleton (which differs from
   *  the test's QueryClientProvider instance). Gets reset when a new row opens. */
  const [overlayMarketLevel, setOverlayMarketLevel] = useState<{
    marketLevel: string;
    cglDisplayName: string;
    confidence: number;
    reasoning: string;
  } | null>(null);

  // Reset local overlay when a different row is opened.
  // biome-ignore lint/correctness/useExhaustiveDependencies: row.id is the trigger
  useEffect(() => {
    setOverlayMarketLevel(null);
  }, [row?.id]);

  async function handleComputeMarketLevel() {
    if (!row?.id) return;
    const result = await inferMarketLevel(row.id);
    if (result) {
      setOverlayMarketLevel(result);
      onMarketLevelComputed?.(row.id, result);
    }
  }

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Compute the reasoning HTML BEFORE any early returns — `useMarkdownHtml`
  // is a hook and must run in the same order on every render. We resolve
  // the displayed market level here too (still safe — both branches handle
  // open=false/row=null gracefully via empty defaults).
  const assessmentEarly = (data?.assessment ?? {}) as { marketLevel?: { reasoning: string } };
  const displayedMarketLevelForHtml = overlayMarketLevel ?? assessmentEarly.marketLevel ?? null;
  const reasoningHtml = useMarkdownHtml(displayedMarketLevelForHtml?.reasoning ?? "");

  if (!open || !row) return null;

  const assessment = data?.assessment ?? {};
  const a = assessment as {
    candidateName?: string;
    appliedLevel?: string;
    summary?: string;
    threeAxis?: {
      decision?: string;
      appliedLevel?: string;
      decisionReason?: string;
      axes?: { id: string; value: number; threshold: number; pass: boolean }[];
    };
    career?: { company?: string; role?: string; period?: string }[];
    education?: { institution?: string; degree?: string; graduated?: number };
    marketLevel?: {
      marketLevel: string;
      cglDisplayName: string;
      confidence: number;
      reasoning: string;
    };
  };
  // Overlay wins: local state set by the infer call takes precedence over
  // whatever came back from the server (which may be stale or absent).
  const displayedMarketLevel = overlayMarketLevel ?? a.marketLevel ?? null;

  return (
    // Click-outside-to-close surface. Was a <button> for keyboard semantics,
    // but the inner card legitimately contains buttons + links — that produced
    // a validateDOMNesting warning on every open. A presentational div gives
    // us the click-outside backdrop without nesting buttons. Esc is wired on
    // the document via the effect above; the dedicated close button inside
    // the card serves keyboard-only users.
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop pattern, redundant with the explicit Fechar button inside the dialog
    <div className="eval-detail-overlay" role="presentation" onClick={onClose}>
      <div
        className="eval-detail-card"
        role="dialog"
        aria-modal="true"
        aria-label={`Detalhes da avaliação de ${row.candidateName ?? "candidato"}`}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <header className="eval-detail-head">
          <div>
            <span className="eval-detail-kicker">Avaliação arquivada</span>
            <h3 className="eval-detail-name">{row.candidateName ?? "Sem nome"}</h3>
            <p className="eval-detail-sub">
              {disciplineLabel(row.disciplineSlug)} · {humanizeAge(row.createdAt)}
              {row.createdBy ? ` · ${row.createdBy}` : ""}
            </p>
          </div>
          <div className="eval-detail-actions">
            <button
              type="button"
              className="eval-detail-cta"
              onClick={() => {
                onOpenReport(row.id);
                onClose();
              }}
              data-testid={`eval-detail-open-report-${row.id}`}
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Abrir relatório completo
            </button>
            <a
              href={`/api/labs/evaluations/${row.id}`}
              target="_blank"
              rel="noreferrer"
              className="eval-detail-raw"
              title="JSON bruto"
            >
              JSON
            </a>
            <button
              type="button"
              className="eval-detail-close"
              onClick={onClose}
              aria-label="Fechar"
            >
              ×
            </button>
          </div>
        </header>

        {isLoading && (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {data && (
          <div className="eval-detail-body">
            <section className="eval-detail-section">
              <h4>Recomendação</h4>
              <dl className="eval-detail-grid">
                <div>
                  <dt>Decisão</dt>
                  <dd
                    className={
                      isInterviewDecision(a.threeAxis?.decision)
                        ? "eval-detail-decision ok"
                        : "eval-detail-decision no"
                    }
                  >
                    {isInterviewDecision(a.threeAxis?.decision) ? "Entrevistar" : "Não entrevistar"}
                  </dd>
                </div>
                <div>
                  <dt>Nível aplicado</dt>
                  <dd>{a.threeAxis?.appliedLevel ?? a.appliedLevel ?? "—"}</dd>
                </div>
              </dl>
              {a.threeAxis?.decisionReason && (
                <p className="eval-detail-note">{a.threeAxis.decisionReason}</p>
              )}
              {a.summary && <p className="eval-detail-note">{a.summary}</p>}
            </section>

            {Array.isArray(a.threeAxis?.axes) && a.threeAxis.axes.length > 0 && (
              <section className="eval-detail-section">
                <h4>Eixos</h4>
                <div className="eval-detail-axes">
                  {a.threeAxis.axes.map((ax) => (
                    <div key={ax.id} className={`eval-axis${ax.pass ? " pass" : " fail"}`}>
                      <span className="eval-axis-id">{ax.id}</span>
                      <span className="eval-axis-value">
                        {typeof ax.value === "number" ? ax.value.toFixed(2) : "—"}
                      </span>
                      <span className="eval-axis-threshold">
                        mín {typeof ax.threshold === "number" ? ax.threshold.toFixed(2) : "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {Array.isArray(a.career) && a.career.length > 0 && (
              <section className="eval-detail-section">
                <h4>Carreira</h4>
                <ul className="eval-detail-career">
                  {a.career.slice(0, 8).map((c, i) => (
                    <li
                      // biome-ignore lint/suspicious/noArrayIndexKey: career order is stable within a fetched assessment
                      key={`career-${i}`}
                    >
                      <span className="eval-career-role">{c.role ?? "—"}</span>
                      <span className="eval-career-company">{c.company ?? "—"}</span>
                      <span className="eval-career-period">{c.period ?? ""}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {a.education?.institution && (
              <section className="eval-detail-section">
                <h4>Formação</h4>
                <p className="eval-detail-note">
                  <strong>{a.education.institution}</strong>
                  {a.education.degree ? ` · ${a.education.degree}` : ""}
                  {a.education.graduated ? ` · ${a.education.graduated}` : ""}
                </p>
              </section>
            )}

            <section className="eval-detail-section" data-testid="eval-detail-market-level-section">
              <h4>Nível de mercado</h4>
              {displayedMarketLevel ? (
                <div
                  className="eval-detail-market-level"
                  data-testid="eval-detail-market-level-block"
                >
                  <div
                    className="eval-detail-market-level-badge"
                    title={`${displayedMarketLevel.cglDisplayName} · ${displayedMarketLevel.marketLevel}`}
                  >
                    <span
                      className="eval-detail-market-level-cgl"
                      data-testid="eval-detail-market-level-cgl"
                    >
                      {displayedMarketLevel.cglDisplayName}
                    </span>
                    <span
                      className="eval-detail-market-level-conf"
                      data-testid="eval-detail-market-level-confidence"
                    >
                      {Math.round(displayedMarketLevel.confidence * 100)}% confiança
                    </span>
                  </div>
                  {/* Reasoning rendered as markdown so paragraph breaks (blank
                      lines), lists, bold and italic display correctly. Same
                      pattern as MarketLevelSection in candidate-intelligence. */}
                  <div
                    className="eval-detail-note eval-detail-market-level-reasoning"
                    data-testid="eval-detail-market-level-reasoning"
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: rendered output is from a Zod-validated LLM string passed through marked; same pattern as frameworks-section.tsx
                    dangerouslySetInnerHTML={{ __html: reasoningHtml }}
                  />
                </div>
              ) : (
                <div
                  className="eval-detail-market-level-empty"
                  data-testid="eval-detail-market-level-empty"
                >
                  <p className="eval-detail-note text-muted-foreground">
                    Inferência de nível de mercado ainda não foi computada.
                  </p>
                  <button
                    type="button"
                    className="eval-detail-market-level-compute-btn"
                    onClick={handleComputeMarketLevel}
                    disabled={mlLoading}
                    data-testid="eval-detail-compute-market-level-btn"
                  >
                    {mlLoading ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin mr-1.5 inline" />
                        Computando…
                      </>
                    ) : (
                      "Computar agora"
                    )}
                  </button>
                  {mlError && (
                    <p
                      className="eval-detail-market-level-error"
                      data-testid="eval-detail-market-level-error"
                    >
                      {mlError}
                    </p>
                  )}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
